import { themes } from "@caliu/styles/themes";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "../ui/Button";
import { ProTag } from "../ui/ProTag";

/** Code, not copy: the same in every language. */
const CODE_SAMPLE = "fn foo() {";

/**
 * A theme swatch drawn in that theme's own colours (native ThemePreviewCard):
 * an inset sidebar thumbnail with the accent dot, a sample of prose, code and
 * a tag, then the theme's name over its footer. Used by Settings → Theme and
 * onboarding. Fonts follow the user's own (`--font-*`), themes carry none.
 */
export function ThemePreviewCard({
  theme,
  isDark,
  active,
  locked,
  onClick,
  className,
}: {
  theme: (typeof themes)[number];
  isDark: boolean;
  active: boolean;
  locked?: boolean;
  onClick: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const palette = isDark ? theme.dark : theme.light;
  const name = isDark ? theme.darkName : theme.lightName;
  const altName = isDark ? theme.lightName : theme.darkName;

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "h-auto flex-col items-stretch justify-start gap-0 overflow-hidden rounded-[14px] border-2 p-0 text-left font-normal",
        "hover:bg-transparent active:scale-[0.98] active:bg-transparent",
        className,
      )}
      style={{
        backgroundColor: palette.bg,
        borderColor: active ? palette.accent : palette.border,
      }}
    >
      {locked && <ProTag className="absolute right-2 top-2 z-10 text-[12px] shadow-sm" />}
      <div className="flex min-h-[132px] flex-1 gap-4 p-1.5">
        {/* Sidebar thumbnail */}
        <div
          className="flex w-[22%] max-w-[72px] shrink-0 flex-col gap-1.5 rounded-[10px] px-2.5 py-3"
          style={{ backgroundColor: palette.sidebar, boxShadow: `0 2px 6px ${palette.shadow}` }}
        >
          <span className="h-[3px] w-3/5 rounded-full" style={{ backgroundColor: palette["text-muted"], opacity: 0.45 }} />
          <span className="h-[3px] w-4/5 rounded-full" style={{ backgroundColor: palette.accent, opacity: 0.6 }} />
          <span className="h-[3px] w-3/5 rounded-full" style={{ backgroundColor: palette["text-muted"], opacity: 0.3 }} />
          <span className="h-[3px] w-2/5 rounded-full" style={{ backgroundColor: palette["text-muted"], opacity: 0.3 }} />
          <span className="mt-auto h-2 w-2 rounded-full" style={{ backgroundColor: palette.accent }} />
        </div>
        {/* Sample content */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 py-2.5 pr-3">
          <span
            className="truncate font-prose text-[15px] font-bold leading-tight"
            style={{ color: palette.text }}
          >
            {t("sidebar.samplePeep")}
          </span>
          <span
            className="line-clamp-2 font-prose text-[12.5px] leading-snug"
            style={{ color: palette["text-secondary"] }}
          >
            {t("sidebar.sampleQuiet")}
          </span>
          <span
            className="w-fit rounded px-1.5 py-0.5 font-code text-[10.5px]"
            style={{ color: palette["text-secondary"], backgroundColor: palette.hover }}
          >
            {CODE_SAMPLE}
          </span>
          <span className="font-chrome text-[12px]" style={{ color: palette.accent }}>
            #notes
          </span>
        </div>
      </div>
      {/* Name */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 font-chrome"
        style={{ backgroundColor: palette.sidebar }}
      >
        <span className="truncate text-[14px] font-bold" style={{ color: palette.text }}>
          {name}
        </span>
        {altName !== name && (
          <span className="ml-auto truncate text-[13px]" style={{ color: palette["text-muted"] }}>
            {altName}
          </span>
        )}
      </div>
    </Button>
  );
}
